import type { Block } from '@emailforge/schemas';
import { Input, Select, Textarea } from '@emailforge/ui';
import { useState } from 'react';

import { Icon } from '@/components/ui/icons';
import { AssetLibraryDrawer } from '@/components/uploads/AssetLibraryDrawer';
import { ImageUploadField } from '@/components/uploads/ImageUploadField';
import { updateBlockProps } from '@/features/editor/editorSlice';
import { useAppDispatch } from '@/store/hooks';

export function Inspector({ block }: { block: Block | null }) {
  const dispatch = useAppDispatch();
  const [libraryFor, setLibraryFor] = useState<'imageUrl' | 'src' | null>(null);

  if (!block) {
    return (
      <div
        style={{
          padding: 24,
          color: 'var(--ink-3)',
          fontSize: 12.5,
          fontFamily: 'var(--font-mono)',
          textAlign: 'center',
        }}
      >
        Select a block to edit
      </div>
    );
  }

  const update = (patch: Record<string, unknown>) => dispatch(updateBlockProps({ blockId: block.id, props: patch }));

  return (
    <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 18 }}>
      <Header type={block.type} />

      {block.type === 'hero' && (
        <Group label="Content">
          <TextAreaField label="Title" value={block.props.title} onChange={(v) => update({ title: v })} />
          <TextAreaField label="Subtitle" value={block.props.subtitle ?? ''} onChange={(v) => update({ subtitle: v })} />
          <div>
            <Label>Image</Label>
            <ImageUploadField
              value={block.props.imageUrl ?? ''}
              onChange={(v) => update({ imageUrl: v })}
              onOpenLibrary={() => setLibraryFor('imageUrl')}
            />
          </div>
          <SelectField
            label="Alignment"
            value={block.props.alignment ?? 'center'}
            options={[
              { value: 'left', label: 'Left' },
              { value: 'center', label: 'Center' },
            ]}
            onChange={(v) => update({ alignment: v })}
          />
        </Group>
      )}

      {block.type === 'heading' && (
        <Group label="Content">
          <TextField label="Text" value={block.props.text} onChange={(v) => update({ text: v })} />
          <SelectField
            label="Level"
            value={block.props.level ?? 'h2'}
            options={[
              { value: 'h1', label: 'H1 (largest)' },
              { value: 'h2', label: 'H2' },
              { value: 'h3', label: 'H3' },
            ]}
            onChange={(v) => update({ level: v })}
          />
          <AlignField value={block.props.alignment ?? 'left'} onChange={(v) => update({ alignment: v })} />
        </Group>
      )}

      {block.type === 'text' && (
        <Group label="Content">
          <TextAreaField label="Text" value={block.props.text} onChange={(v) => update({ text: v })} />
          <SelectField
            label="Size"
            value={block.props.size ?? 'md'}
            options={[
              { value: 'xs', label: 'XS' },
              { value: 'sm', label: 'SM' },
              { value: 'md', label: 'MD' },
              { value: 'lg', label: 'LG' },
            ]}
            onChange={(v) => update({ size: v })}
          />
          <AlignField value={block.props.alignment ?? 'left'} onChange={(v) => update({ alignment: v })} />
        </Group>
      )}

      {block.type === 'button' && (
        <Group label="Content">
          <TextField label="Label" value={block.props.label} onChange={(v) => update({ label: v })} />
          <TextField label="URL" value={block.props.href} onChange={(v) => update({ href: v })} />
          <SelectField
            label="Variant"
            value={block.props.variant ?? 'primary'}
            options={[
              { value: 'primary', label: 'Primary (flame)' },
              { value: 'secondary', label: 'Secondary (ink)' },
              { value: 'ghost', label: 'Ghost' },
            ]}
            onChange={(v) => update({ variant: v })}
          />
          <AlignField value={block.props.alignment ?? 'center'} onChange={(v) => update({ alignment: v })} />
          <ToggleField
            label="Full width"
            value={!!block.props.fullWidth}
            onChange={(v) => update({ fullWidth: v })}
          />
        </Group>
      )}

      {block.type === 'image' && (
        <Group label="Content">
          <div>
            <Label>Image</Label>
            <ImageUploadField
              value={block.props.src}
              onChange={(v) => update({ src: v })}
              onOpenLibrary={() => setLibraryFor('src')}
            />
          </div>
          <TextField label="Alt" value={block.props.alt ?? ''} onChange={(v) => update({ alt: v })} />
          <TextField label="Link URL" value={block.props.href ?? ''} onChange={(v) => update({ href: v })} />
          <NumberField
            label="Width (px)"
            value={block.props.width ?? 600}
            onChange={(v) => update({ width: v })}
          />
        </Group>
      )}

      {block.type === 'divider' && (
        <Group label="Style">
          <NumberField
            label="Thickness"
            value={block.props.thickness ?? 1}
            onChange={(v) => update({ thickness: v })}
            min={1}
            max={4}
          />
          <TextField label="Color" value={block.props.color ?? ''} onChange={(v) => update({ color: v })} />
        </Group>
      )}

      {block.type === 'spacer' && (
        <Group label="Style">
          <NumberField
            label="Height (px)"
            value={block.props.height}
            onChange={(v) => update({ height: v })}
            min={4}
            max={128}
          />
        </Group>
      )}

      {block.type === 'product' && (
        <Group label="Product">
          <TextField label="Name" value={block.props.name} onChange={(v) => update({ name: v })} />
          <TextField label="Price" value={block.props.price} onChange={(v) => update({ price: v })} />
          <div>
            <Label>Image</Label>
            <ImageUploadField
              value={block.props.imageUrl}
              onChange={(v) => update({ imageUrl: v })}
              onOpenLibrary={() => setLibraryFor('imageUrl')}
            />
          </div>
          <TextField label="Link URL" value={block.props.href} onChange={(v) => update({ href: v })} />
          <TextAreaField
            label="Description"
            value={block.props.description ?? ''}
            onChange={(v) => update({ description: v })}
          />
        </Group>
      )}

      {block.type === 'footer' && (
        <Group label="Footer">
          <TextField
            label="Company name"
            value={block.props.companyName}
            onChange={(v) => update({ companyName: v })}
          />
          <TextField label="Address" value={block.props.address ?? ''} onChange={(v) => update({ address: v })} />
          <TextField
            label="Unsubscribe URL"
            value={block.props.unsubscribeUrl}
            onChange={(v) => update({ unsubscribeUrl: v })}
          />
        </Group>
      )}

      {block.type === 'columns' && (
        <Group label="Layout">
          <SelectField
            label="Ratio"
            value={block.props.ratio}
            options={[
              { value: '1:1', label: '1 : 1' },
              { value: '1:2', label: '1 : 2' },
              { value: '2:1', label: '2 : 1' },
            ]}
            onChange={(v) => update({ ratio: v })}
          />
          <NumberField
            label="Gap"
            value={block.props.gap}
            onChange={(v) => update({ gap: v })}
            min={0}
            max={48}
          />
        </Group>
      )}

      {'props' in block && 'paddingTop' in (block.props as object) && (
        <Group label="Spacing">
          <NumberField
            label="Padding top"
            value={((block.props as { paddingTop?: number }).paddingTop ?? 0) as number}
            onChange={(v) => update({ paddingTop: v })}
            min={0}
            max={128}
          />
          <NumberField
            label="Padding bottom"
            value={((block.props as { paddingBottom?: number }).paddingBottom ?? 0) as number}
            onChange={(v) => update({ paddingBottom: v })}
            min={0}
            max={128}
          />
        </Group>
      )}

      {'props' in block && 'darkMode' in (block.props as object) && (
        <DarkModeGroup
          props={
            block.props as {
              darkMode?: boolean;
              darkBackgroundColor?: string;
              darkColor?: string;
              backgroundColor?: string;
              color?: string;
            }
          }
          onChange={update}
        />
      )}

      {libraryFor && (
        <AssetLibraryDrawer
          open={true}
          onClose={() => setLibraryFor(null)}
          onPick={(url) => {
            update({ [libraryFor]: url });
            setLibraryFor(null);
          }}
        />
      )}
    </div>
  );
}

/**
 * Inspector subgroup for dark-mode overrides. Off by default — emails carry
 * their light-mode inline styles regardless. When toggled on, the renderer
 * emits a `@media (prefers-color-scheme: dark)` rule that swaps these
 * colors on Apple Mail / Gmail web/iOS / Outlook web.
 */
function DarkModeGroup({
  props,
  onChange,
}: {
  props: {
    darkMode?: boolean;
    darkBackgroundColor?: string;
    darkColor?: string;
    backgroundColor?: string;
    color?: string;
  };
  onChange: (patch: Record<string, unknown>) => void;
}) {
  const enabled = Boolean(props.darkMode);
  return (
    <Group label="Dark-mode variant">
      <div
        style={{
          padding: 10,
          borderRadius: 'var(--r-2)',
          border: `1px solid ${enabled ? 'var(--flame-line)' : 'var(--line-soft)'}`,
          background: enabled ? 'var(--flame-soft)' : 'var(--paper-2)',
          display: 'flex',
          flexDirection: 'column',
          gap: 10,
        }}
      >
        <label
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            fontSize: 12.5,
            fontWeight: 550,
            cursor: 'pointer',
          }}
        >
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => onChange({ darkMode: e.target.checked })}
          />
          <Icon.moon size={12} />
          Apply dark-mode overrides
        </label>

        <div className="mono" style={{ fontSize: 10, color: 'var(--ink-3)', lineHeight: 1.5 }}>
          Toggle the canvas to dark in the toolbar to preview. Honored by Apple Mail, Gmail
          web/iOS, Outlook web. Light-mode colors apply everywhere else.
        </div>

        {enabled && (
          <>
            <DarkColorPair
              lightLabel="Light bg"
              lightValue={props.backgroundColor ?? '#ffffff'}
              darkLabel="Dark bg"
              darkValue={props.darkBackgroundColor ?? '#1a1a1a'}
              onDarkChange={(v) => onChange({ darkBackgroundColor: v || undefined })}
            />
            <DarkColorPair
              lightLabel="Light text"
              lightValue={props.color ?? '#0e0e0e'}
              darkLabel="Dark text"
              darkValue={props.darkColor ?? '#f3f3f3'}
              onDarkChange={(v) => onChange({ darkColor: v || undefined })}
            />
            <DarkPreview
              lightBg={props.backgroundColor ?? '#ffffff'}
              lightFg={props.color ?? '#0e0e0e'}
              darkBg={props.darkBackgroundColor ?? '#1a1a1a'}
              darkFg={props.darkColor ?? '#f3f3f3'}
            />
          </>
        )}
      </div>
    </Group>
  );
}

function DarkColorPair({
  lightLabel,
  lightValue,
  darkLabel,
  darkValue,
  onDarkChange,
}: {
  lightLabel: string;
  lightValue: string;
  darkLabel: string;
  darkValue: string;
  onDarkChange: (v: string) => void;
}) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
      <div>
        <Label>{lightLabel}</Label>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            padding: '4px 6px',
            border: '1px solid var(--line)',
            borderRadius: 'var(--r-2)',
            background: 'var(--paper)',
          }}
        >
          <span
            style={{
              width: 16,
              height: 16,
              borderRadius: 3,
              border: '1px solid var(--line)',
              background: lightValue,
            }}
          />
          <span className="mono" style={{ fontSize: 11, color: 'var(--ink-2)' }}>
            {lightValue}
          </span>
        </div>
      </div>
      <div>
        <Label>{darkLabel}</Label>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            padding: '2px 6px',
            border: '1px solid var(--line)',
            borderRadius: 'var(--r-2)',
            background: 'var(--paper)',
          }}
        >
          <input
            type="color"
            value={normalizeHex(darkValue)}
            onChange={(e) => onDarkChange(e.target.value)}
            style={{
              width: 22,
              height: 22,
              padding: 0,
              border: 'none',
              background: 'transparent',
              cursor: 'pointer',
            }}
          />
          <Input
            type="text"
            value={darkValue}
            onChange={(e) => onDarkChange(e.target.value)}
            style={{ flex: 1, fontSize: 11, fontFamily: 'var(--font-mono)' }}
          />
        </div>
      </div>
    </div>
  );
}

function DarkPreview({
  lightBg,
  lightFg,
  darkBg,
  darkFg,
}: {
  lightBg: string;
  lightFg: string;
  darkBg: string;
  darkFg: string;
}) {
  return (
    <div>
      <Label>Side-by-side preview</Label>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
        <div
          style={{
            padding: 10,
            background: lightBg,
            color: lightFg,
            borderRadius: 4,
            fontSize: 11,
            border: '1px solid var(--line)',
            textAlign: 'center',
          }}
        >
          <div className="mono" style={{ fontSize: 9, opacity: 0.6, marginBottom: 4 }}>
            LIGHT
          </div>
          The quick brown fox
        </div>
        <div
          style={{
            padding: 10,
            background: darkBg,
            color: darkFg,
            borderRadius: 4,
            fontSize: 11,
            border: '1px solid var(--line)',
            textAlign: 'center',
          }}
        >
          <div className="mono" style={{ fontSize: 9, opacity: 0.6, marginBottom: 4 }}>
            DARK
          </div>
          The quick brown fox
        </div>
      </div>
    </div>
  );
}

function normalizeHex(value: string): string {
  if (/^#[0-9a-f]{6}$/i.test(value)) return value;
  if (/^#[0-9a-f]{3}$/i.test(value)) {
    return `#${value
      .slice(1)
      .split('')
      .map((c) => c + c)
      .join('')}`;
  }
  return '#000000';
}

function Header({ type }: { type: string }) {
  return (
    <div>
      <div
        className="mono"
        style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.08, color: 'var(--ink-3)' }}
      >
        Inspector
      </div>
      <div style={{ fontSize: 15, fontWeight: 600, marginTop: 4, textTransform: 'capitalize' }}>{type}</div>
    </div>
  );
}

function Group({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div
        className="mono"
        style={{
          fontSize: 10,
          textTransform: 'uppercase',
          letterSpacing: 0.08,
          color: 'var(--ink-3)',
          marginBottom: 8,
        }}
      >
        {label}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>{children}</div>
    </div>
  );
}

function Label({ children }: { children: React.ReactNode }) {
  return (
    <div
      className="mono"
      style={{
        fontSize: 10,
        color: 'var(--ink-3)',
        textTransform: 'uppercase',
        letterSpacing: 0.06,
        marginBottom: 4,
      }}
    >
      {children}
    </div>
  );
}

function TextField({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  return (
    <div>
      <Label>{label}</Label>
      <Input  value={value} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}

function TextAreaField({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  return (
    <div>
      <Label>{label}</Label>
      <Textarea
        
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{ minHeight: 60, padding: '6px 10px', resize: 'vertical', height: 'auto', lineHeight: 1.5 }} />
    </div>
  );
}

function NumberField({
  label,
  value,
  onChange,
  min,
  max,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
  min?: number;
  max?: number;
}) {
  return (
    <div>
      <Label>{label}</Label>
      <Input
        
        type="number"
        value={value}
        min={min}
        max={max}
        onChange={(e) => onChange(Number(e.target.value))} />
    </div>
  );
}

function SelectField<T extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: T;
  options: Array<{ value: T; label: string }>;
  onChange: (v: T) => void;
}) {
  return (
    <div>
      <Label>{label}</Label>
      <Select  value={value} onChange={(e) => onChange(e.target.value as T)}>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </Select>
    </div>
  );
}

function AlignField({ value, onChange }: { value: 'left' | 'center' | 'right'; onChange: (v: 'left' | 'center' | 'right') => void }) {
  return (
    <SelectField
      label="Alignment"
      value={value}
      options={[
        { value: 'left', label: 'Left' },
        { value: 'center', label: 'Center' },
        { value: 'right', label: 'Right' },
      ]}
      onChange={onChange}
    />
  );
}

function ToggleField({ label, value, onChange }: { label: string; value: boolean; onChange: (v: boolean) => void }) {
  return (
    <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: 'var(--ink-2)' }}>
      <input type="checkbox" checked={value} onChange={(e) => onChange(e.target.checked)} />
      {label}
    </label>
  );
}
